import React from 'react';
import {observable, action, computed, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../s';
import * as data from '../../data';
import {checkout, klaviyo_identify} from '../../../api';
import {CheckoutInputElement} from '../inputs/checkoutInputs';
import {isNullOrWhiteSpace, Portal} from '../../../util';

@observer
class BillingWrite extends React.Component {
    @observable focusedCountry: boolean = false;
    @observable focusedState: boolean = false;
    @observable focusedAddressBook: boolean = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @computed get isCountryWithStates() {
        try {
            return (
                store.checkout.ship.addr.country.value.toLowerCase() === 'us' ||
                store.checkout.ship.addr.country.value.toLowerCase() === 'ca' ||
                store.checkout.ship.addr.country.value.toLowerCase() === 'jp' ||
                store.checkout.ship.addr.country.value.toLowerCase() === 'gb' ||
                store.checkout.ship.addr.country.value.toLowerCase() === 'de'
            );
        } catch {
            return true;
        }
    }

    async componentDidMount() {
        this.initShipAddressBook();
    }

    @action
    initShipAddressBook = () => {
        let initBilling = store.checkout.isMyFrames && store.checkout.AddressBookAddresses != null && store.checkout.AddressBookAddresses.length > 0;
        if (!initBilling) {
            initBilling = store.checkout.AddressBookAddresses != null && store.checkout.AddressBookAddresses.length > 0 && store.checkout.billing.AddressID < 1;
        }

        if (initBilling) {
            const a = store.checkout.AddressBookAddresses.filter(x => x.isBilling);
            if (a.length > 0) {
                store.checkout.billing.AddressID = a[0].AddressID;
                store.setBillingAddressToBook();
            }
        }
    };

    @action
    handleChangeAddressBook = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        store.checkout.billing.AddressID = parseInt(e.target.value, 10);
        store.setBillingAddressToBook();
    };

    @action
    save = async () => {
        if (store.checkout.billing.addr.state.value === 'AL1') {
            store.checkout.billing.addr.state.valid.isError = true;
            store.checkout.billing.addr.state.valid.msg = 'Please select a state/region';
            return;
        }

        const success = await checkout('cc-billing-addr-submit');
        try {
            await klaviyo_identify();
        } catch (errr) {
            console.error(errr);
        }
    };

    @action
    syncMore = async () => {
        store.checkout.billing.addr.addrShowMore = true;
        await checkout('sync-billing-more');
    };

    render() {
        return (
            <div className="billing-write">
                <div className="">
                    <div className="row">
                        <div className="col-12 pad-top">
                            <div className="ship-methods-header">Enter billing address</div>
                        </div>
                    </div>

                    {/* Address Book */}
                    {store.checkout.AddressBookAddresses != null && store.checkout.AddressBookAddresses.length > 0 && (
                        <div className={'col-12 mb-2'}>
                            {store.checkout.ab < 0 && <div className={this.focusedAddressBook ? 'input-label input-label-focused' : 'input-label'}>Address Book</div>}
                            <div className="form-floating">
                                <select
                                    className="form-select"
                                    name="billing-address-book"
                                    onFocus={() => (this.focusedAddressBook = true)}
                                    onBlur={() => (this.focusedAddressBook = false)}
                                    autoComplete="off"
                                    value={store.checkout.billing.AddressID}
                                    aria-label="Address Book"
                                    onChange={this.handleChangeAddressBook}
                                >
                                    <option
                                        value={-1}
                                        key={-1}
                                    >
                                        Select Address
                                    </option>
                                    {store.checkout.AddressBookAddresses.map(e => (
                                        <option
                                            value={e.AddressID}
                                            key={e.AddressID}
                                        >
                                            {e.Nickname}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="billing-address-book">Address Book</label>
                            </div>
                        </div>
                    )}

                    {/* first and last names */}
                    <div className="row">
                        <div className="col-12 col-sm-6">
                            <CheckoutInputElement
                                floating={true}
                                ab={store.checkout.ab}
                                o={store.checkout.billing.addr.firstName}
                                label="First Name"
                                ariaLabel="Billing First Name"
                                required={true}
                                billing={true}
                            />
                        </div>
                        <div className="col-12 col-sm-6">
                            <CheckoutInputElement
                                floating={true}
                                ab={store.checkout.ab}
                                o={store.checkout.billing.addr.lastName}
                                required={true}
                                ariaLabel="Billing Last Name"
                                billing={true}
                            />
                        </div>
                    </div>

                    {/* companyName */}
                    {store.checkout.billing.addr.addrShowMore && (
                        <div className="row">
                            <div className="col-12">
                                <CheckoutInputElement
                                    floating={true}
                                    ab={store.checkout.ab}
                                    o={store.checkout.billing.addr.companyName}
                                    required={false}
                                    ariaLabel="Billing Company Name"
                                    billing={true}
                                />
                            </div>
                        </div>
                    )}

                    {/* address line 1 */}
                    <div className="row">
                        <div className="col-12">
                            <CheckoutInputElement
                                floating={true}
                                ab={store.checkout.ab}
                                o={store.checkout.billing.addr.address1}
                                required={true}
                                ariaLabel="Billing Address Line 1"
                                billing={true}
                            />
                        </div>
                    </div>

                    {/* address line 2 */}
                    <div className="row">
                        {store.checkout.billing.addr.addrShowMore && (
                            <div className="col-12">
                                <CheckoutInputElement
                                    floating={true}
                                    ab={store.checkout.ab}
                                    o={store.checkout.billing.addr.address2}
                                    required={false}
                                    ariaLabel="Billing Address Line 2"
                                    billing={true}
                                />
                            </div>
                        )}
                        {!store.checkout.billing.addr.addrShowMore && (
                            <div className="col-12">
                                <button
                                    className="add-more text-btn"
                                    onClick={this.syncMore}
                                    type="button"
                                    aria-label="Add billing company or billing address line 2"
                                >
                                    + Add Company, C/O, Apt, Suite, Unit
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="row pad-top">
                        {/* city */}
                        <div className={this.isCountryWithStates ? 'col-12 col-sm-4 col-lg-4' : 'col-12 col-sm-4'}>
                            <CheckoutInputElement
                                floating={true}
                                ab={store.checkout.ab}
                                o={store.checkout.billing.addr.city}
                                required={true}
                                ariaLabel="Billing City"
                                billing={true}
                            />
                        </div>

                        {/* state */}
                        <div className={this.isCountryWithStates ? 'col-12 col-sm-5 col-lg-3' : 'col-12 col-sm-4'}>
                            {store.checkout.ab < 0 && <div className={this.focusedState ? 'input-label input-label-focused' : 'input-label'}>* State/Region</div>}
                            {this.isCountryWithStates && (
                                <div className="form-floating">
                                    <select
                                        name="billing-states"
                                        className="form-control"
                                        value={store.checkout.billing.addr.state.value}
                                        onChange={e => {
                                            store.checkout.billing.addr.state.value = e.target.value;
                                            store.checkout.billing.addr.state.valid.isError = false;
                                        }}
                                        onFocus={() => (this.focusedState = true)}
                                        onBlur={() => (this.focusedState = false)}
                                        autoComplete="billing address-level1"
                                        aria-label="Billing State or Region"
                                    >
                                        {store.checkout.billing.addr.country.value === 'US' &&
                                            data.states
                                                .filter(e => e.cid === 217)
                                                .map(e => (
                                                    <option
                                                        value={e.code}
                                                        key={e.id}
                                                    >
                                                        {e.name}
                                                    </option>
                                                ))}
                                        {store.checkout.billing.addr.country.value === 'CA' &&
                                            data.states
                                                .filter(e => e.cid === 39)
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map(e => (
                                                    <option
                                                        value={e.code}
                                                        key={e.id}
                                                    >
                                                        {e.name}
                                                    </option>
                                                ))}
                                        {store.checkout.billing.addr.country.value === 'DE' &&
                                            data.states
                                                .filter(e => e.cid === 78)
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map(e => (
                                                    <option
                                                        value={e.code}
                                                        key={e.id}
                                                    >
                                                        {e.name}
                                                    </option>
                                                ))}
                                        {store.checkout.billing.addr.country.value === 'GB' &&
                                            data.states
                                                .filter(e => e.cid === 216)
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map(e => (
                                                    <option
                                                        value={e.code}
                                                        key={e.id}
                                                    >
                                                        {e.name}
                                                    </option>
                                                ))}
                                        {store.checkout.billing.addr.country.value === 'JP' &&
                                            data.states
                                                .filter(e => e.cid === 104)
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map(e => (
                                                    <option
                                                        value={e.code}
                                                        key={e.id}
                                                    >
                                                        {e.name}
                                                    </option>
                                                ))}
                                    </select>
                                    <label htmlFor="billing-states">State or Region</label>
                                </div>
                            )}
                            {store.checkout.billing.addr.state.valid.isError && (
                                <div
                                    className="input-error"
                                    style={{paddingTop: 4}}
                                >
                                    {store.checkout.billing.addr.state.valid.msg}
                                </div>
                            )}
                            {!this.isCountryWithStates && (
                                <input
                                    value={store.checkout.billing.addr.state.value}
                                    onChange={e => (store.checkout.billing.addr.state.value = e.target.value)}
                                    onFocus={() => (this.focusedState = true)}
                                    onBlur={() => (this.focusedState = false)}
                                    className="form-control"
                                    placeholder="State/Region"
                                    type="text"
                                    autoComplete="billing address-level1"
                                    aria-label="Billing State or Region"
                                />
                            )}
                        </div>

                        {/* postal code */}
                        <div className={this.isCountryWithStates ? 'col-12 col-sm-3 col-lg-5 col-postal-code' : 'col-12 col-sm-4 col-postal-code'}>
                            <CheckoutInputElement
                                floating={true}
                                ab={store.checkout.ab}
                                o={store.checkout.billing.addr.postalCode}
                                type="text"
                                required={true}
                                hideSpinner={true}
                                ariaLabel="Billing Postal Code"
                                billing={true}
                            />
                        </div>

                        {/* country */}
                        <div className={'col-12 mb-2'}>
                            {store.checkout.ab < 0 && <div className={this.focusedCountry ? 'input-label input-label-focused' : 'input-label'}>* Country</div>}
                            <div className="form-floating">
                                <select
                                    className="form-control"
                                    name="billing-country"
                                    onFocus={() => (this.focusedCountry = true)}
                                    onBlur={() => (this.focusedCountry = false)}
                                    autoComplete="billing country"
                                    aria-label="Billing Country"
                                    value={store.checkout.billing.addr.country.value}
                                    onChange={e => {
                                        store.checkout.billing.addr.country.value = e.target.value;
                                        store.checkout.billing.addr.state.valid.isError = false;
                                    }}
                                >
                                    {data.countries.map(e => (
                                        <option
                                            value={e.code}
                                            key={e.id}
                                        >
                                            {e.name}
                                        </option>
                                    ))}
                                </select>
                                <label htmlFor="billing-country">Country</label>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12 text-right pad-top">
                            <button
                                type="button"
                                className={`btn-checkout btn-checkout-yellow${Portal?.PortalID === 3 ? ' btn-checkout-yellow-portal3' : ''}`}
                                onClick={this.save}
                                aria-label="Save and continue"
                            >
                                Save &amp; Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

@observer
class BillingReadOnly extends React.Component {
    constructor(props) {
        super(props);
    }

    render() {
        return (
            <div className="pad-top">
                <div className={store.checkout.cstep === ICheckoutStep.shipping_methods ? 'billing-read billing-read-options' : 'billing-read'}>
                    {!store.checkout.billing.addrSameAsShip && (
                        <div className="row">
                            <div className="col-12">
                                {store.checkout.billing.addr.firstName.value}&nbsp;
                                {store.checkout.billing.addr.lastName.value}
                            </div>
                            {!isNullOrWhiteSpace(store.checkout.billing.addr.companyName.value) && <div className="col-12">{store.checkout.billing.addr.companyName.value}</div>}
                            <div className="col-12">{store.checkout.billing.addr.address1.value}</div>
                            {!isNullOrWhiteSpace(store.checkout.billing.addr.address2.value) && <div className="col-12">{store.checkout.billing.addr.address2.value}</div>}
                            {!isNullOrWhiteSpace(store.checkout.billing.addr.address3.value) && <div className="col-12">{store.checkout.billing.addr.address3.value}</div>}
                            {!isNullOrWhiteSpace(store.checkout.billing.addr.address4.value) && <div className="col-12">{store.checkout.billing.addr.address4.value}</div>}
                            <div className="col-12">
                                {store.checkout.billing.addr.city.value}
                                {store.checkout.billing.addr.state.value.length > 0 && (
                                    <span>
                                        ,&nbsp;
                                        {store.checkout.billing.addr.state.value.startsWith('AE') ? 'AE' : store.checkout.billing.addr.state.value}
                                        &nbsp;
                                    </span>
                                )}
                                &nbsp;{store.checkout.billing.addr.postalCode.value}&nbsp;
                                {store.checkout.billing.addr.country.value}
                            </div>
                        </div>
                    )}
                    {store.checkout.billing.addrSameAsShip && (
                        <div className="row">
                            <div className="col-12">
                                {store.checkout.ship.addr.firstName.value}&nbsp;
                                {store.checkout.ship.addr.lastName.value}
                            </div>
                            {!isNullOrWhiteSpace(store.checkout.ship.addr.companyName.value) && <div className="col-12">{store.checkout.ship.addr.companyName.value}</div>}
                            <div className="col-12">{store.checkout.ship.addr.address1.value}</div>
                            {!isNullOrWhiteSpace(store.checkout.ship.addr.address2.value) && <div className="col-12">{store.checkout.ship.addr.address2.value}</div>}
                            {!isNullOrWhiteSpace(store.checkout.ship.addr.address3.value) && <div className="col-12">{store.checkout.ship.addr.address3.value}</div>}
                            {!isNullOrWhiteSpace(store.checkout.ship.addr.address4.value) && <div className="col-12">{store.checkout.ship.addr.address4.value}</div>}
                            <div className="col-12">
                                {store.checkout.ship.addr.city.value}
                                {store.checkout.ship.addr.state.value.length > 0 && <span>,&nbsp;{store.checkout.ship.addr.state.value}&nbsp;</span>}
                                &nbsp;{store.checkout.ship.addr.postalCode.value}&nbsp;
                                {store.checkout.ship.addr.country.value}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

@observer
class PaymentAddrComponent extends React.Component {
    constructor(props) {
        super(props);
        this.handleBillingShippingToggle = this.handleBillingShippingToggle.bind(this);
    }

    handleBillingShippingToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
        store.checkout.billing.addrSameAsShip = !store.checkout.billing.addrSameAsShip;
        if (store.checkout.billing.addrSameAsShip) {
            store.moveStep(ICheckoutStep.payment);
        } else {
            store.moveStep(ICheckoutStep.paymentAddr);
        }

        store.checkout.billing.addr.firstName.value = store.checkout.ship.addr.firstName.value;
        store.checkout.billing.addr.lastName.value = store.checkout.ship.addr.lastName.value;
        store.checkout.billing.addr.address1.value = store.checkout.ship.addr.address1.value;
        store.checkout.billing.addr.address2.value = store.checkout.ship.addr.address2.value;
        store.checkout.billing.addr.address3.value = store.checkout.ship.addr.address3.value;
        store.checkout.billing.addr.address4.value = store.checkout.ship.addr.address4.value;
        store.checkout.billing.addr.companyName.value = store.checkout.ship.addr.companyName.value;
        store.checkout.billing.addr.isBusinessAddress = store.checkout.ship.addr.isBusinessAddress;
        store.checkout.billing.addr.city.value = store.checkout.ship.addr.city.value;
        store.checkout.billing.addr.state.value = store.checkout.ship.addr.state.value;
        store.checkout.billing.addr.country.value = store.checkout.ship.addr.country.value;
        store.checkout.billing.addr.postalCode.value = store.checkout.ship.addr.postalCode.value;
        store.checkout.messages = [];
        store.checkout.billing.messages = [];

        if (store.checkout.billing.addr.companyName.value.startsWith('Armoed Forces')) {
            store.checkout.billing.addr.companyName.value = '';
        }

        await checkout('checkout-sync');
    };

    render() {
        if (!store.checkout.billing.addrSameAsShip) {
            store.checkout.billing.addrSameAsShip = false;
        }

        return (
            <div className="billing-addr">
                <div className="checkout-padding checkout-border">
                    <div className="row">
                        <div className="col-12 required pad-bottom">* Required</div>
                        <div className="col-12 ship-label-3">Billing Address</div>
                    </div>

                    <div className="row">
                        {!store.checkout.billing.addrSameAsShip && (
                            <div className="col-12 form-inline-label form-label-light">
                                {store.checkout.cstep !== ICheckoutStep.paymentAddr && (
                                    <span
                                        className="pad-left link b pointer"
                                        onClick={() => {
                                            store.checkout.billing.addrSameAsShip = false;
                                            store.moveStep(ICheckoutStep.paymentAddr);
                                        }}
                                    >
                                        edit
                                    </span>
                                )}
                            </div>
                        )}
                        {!store.checkout.isMyFrames && (
                            <div
                                className="col-12"
                                style={{fontSize: 12}}
                            >
                                For your protection, this must match what appears on your credit card or bank statement
                            </div>
                        )}
                    </div>
                    {!store.checkout.isMyFrames && (store.checkout.billing.addrSameAsShip || store.checkout.cstep === ICheckoutStep.paymentAddr) && (
                        <div
                            className="row"
                            style={{marginTop: 6, marginBottom: 6}}
                        >
                            <div className="col-12">
                                <label className="radio-container">
                                    <input
                                        type="checkbox"
                                        className="radio radio-sm"
                                        name="billing-address-same-as-ship"
                                        aria-label="Billing Address Matches Shipping Address"
                                        value={1}
                                        checked={store.checkout.billing.addrSameAsShip}
                                        onChange={this.handleBillingShippingToggle}
                                    />
                                    <span className="radio-label">
                                        Billing address same as shipping
                                        {/* {store.checkout.billing.addrSameAsShip && <span className='form-inline-label form-label-light '><span className='pad-left link b pointer'>edit</span></span>} */}
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}
                    {(store.checkout.isMyFrames || !store.checkout.billing.addrSameAsShip) && store.checkout.cstep === ICheckoutStep.paymentAddr && <BillingWrite />}
                    {store.checkout.cstep !== ICheckoutStep.paymentAddr && <BillingReadOnly />}
                    {/* {store.checkout.billing.addrSameAsShip && <ShippingAddrReadOnly hideEdit={true} />} */}
                </div>
            </div>
        );
    }
}
export default PaymentAddrComponent;
